import {
  PreferenceStoreIdentifier,
  PrivacyCenterTrackingPurposePreview,
} from '@main/cm-types';
import {
  FlexRowCenterVertical,
  Form,
  formatErrorMessage,
  message,
  Prompt,
  Skeleton,
} from '@main/core-ui';
import { endpoints } from '@main/pc-types';
import {
  Card,
  RequiredEnabledAirgap,
  StyledColumnContainer,
  StyledPrimaryButton,
  StyledSecondaryButton,
} from '@main/privacy-center/src/components';
import { selectActiveDataSubject } from '@main/privacy-center/src/containers/ActiveDataSubject/selectors';
import { selectCoreIdentifier } from '@main/privacy-center/src/containers/App/selectors';
import {
  buildUseDhMutation,
  usePartition,
} from '@main/privacy-center/src/hooks';
import { sp } from '@main/theme';
import { LanguageKey } from '@transcend-io/internationalization';
import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import {
  Purpose,
  PurposeListForm,
} from '../../../CommunicationsPreferences/components';
import { getUpdateDataSubjectConsentPreferencesInput } from '../../../helpers';
import { privacySettingMessages } from './messages';

const useDataSubjectUpdateConsentPreferences = buildUseDhMutation(
  endpoints.dataSubjectUpdateConsentPreferences,
);

interface UpdatePrivacySettingsFormProps {
  /** current Consent selections */
  consent: Record<string, Purpose>;
  /** configured purposes */
  purposes: PrivacyCenterTrackingPurposePreview[];
  /** Identifiers for the consent record */
  identifiers: PreferenceStoreIdentifier[];
  /** Callback to handle the change of a consent preference */
  onChange: (consent: Record<string, Purpose>) => void;
}

/**
 * Form Component for updating user's privacy settings
 */
export const UpdatePrivacySettingsForm: React.FC<
  UpdatePrivacySettingsFormProps
> = ({ consent, purposes: configuredPurposes, identifiers, onChange }) => {
  const { locale, formatMessage } = useIntl();
  const [{ partitionOptions, selectedPartition }] = usePartition();
  const coreIdentifier = useSelector(selectCoreIdentifier);
  const activeDataSubject = useSelector(selectActiveDataSubject);
  const [unsubscribeAll, setUnsubscribeAll] = useState(false);
  const [isFormReady, setIsFormReady] = useState(false);

  const [updateDataSubjectConsentPreferences, { loading, error }] =
    useDataSubjectUpdateConsentPreferences({
      refetchQueries: [
        'DataSubjectConsentPreferences',
        'RequestHistory',
        'RequestHistoryActiveCount',
      ],
      awaitRefetchQueries: true,
    });

  // Form method with default values
  const formMethods = useForm({
    shouldUnregister: false,
    defaultValues: {
      email: coreIdentifier?.value ?? '',
      purposes: {} as Record<string, Purpose>,
    },
  });

  useEffect(() => {
    formMethods.reset({ purposes: consent });
    setIsFormReady(true);
  }, [consent, formMethods]);

  // Watch for changes in form values
  const formPurposes = formMethods.watch('purposes');

  const regionalizedPurposes = useMemo(
    () =>
      configuredPurposes.filter(
        ({ trackingType }) => consent[trackingType] !== undefined,
      ),
    [configuredPurposes, consent],
  );

  const isSubmitDisabled = useMemo(
    () => !formMethods.formState.isDirty || regionalizedPurposes.length === 0,
    [formMethods.formState.isDirty, regionalizedPurposes],
  );

  // Effect to update the unsubscribeAll state when all purposes are selected
  useEffect(() => {
    const unsubscribe = Object.values(formPurposes).every(
      ({ consent }) => consent === true,
    );
    setUnsubscribeAll(unsubscribe);
  }, [formPurposes]);

  // Effect to show error message if any
  useEffect(() => {
    if (error) {
      message.error(formatErrorMessage(error.message));
    }
  }, [error]);

  /**
   * On form submit, updates communication preferences
   *
   * @param data - form data
   */
  const handleSubmit = async (data: {
    /** email address */
    email: string;
    /** consent selections */
    purposes: Record<string, Purpose>;
  }): Promise<void> => {
    if (
      activeDataSubject &&
      coreIdentifier &&
      updateDataSubjectConsentPreferences
    ) {
      const { input: updatedRow, dhEncryptedInput } =
        getUpdateDataSubjectConsentPreferencesInput(
          coreIdentifier.value,
          identifiers,
          selectedPartition!,
          data.purposes,
        );
      await updateDataSubjectConsentPreferences({
        variables: {
          input: {
            consentPreferences: [updatedRow],
            locale: locale as LanguageKey,
            subjectType: activeDataSubject.type,
          },
        },
        dhEncryptedInput,
      }).then(({ data: updatedResponse }) => {
        if (updatedResponse && updatedResponse.data.length > 0) {
          formMethods.reset();
          message.success(formatMessage(privacySettingMessages.success));
          onChange(
            updatedResponse.data[0].purposes.reduce(
              (acc, { purpose, consent }) => ({
                ...acc,
                [purpose]: {
                  consent,
                },
              }),
              {},
            ),
          );
        }
      });
    }
  };

  /**
   * Opt out/In of all  preferences
   */
  function setAllPreferences(): void {
    const { purposes } = formMethods.getValues();
    const latestSelections = Object.entries(purposes).reduce(
      (acc, [key]) => ({
        ...acc,
        [key]: {
          consent: !unsubscribeAll,
        },
      }),
      {},
    );
    formMethods.setValue('purposes', latestSelections, { shouldDirty: true });
    setUnsubscribeAll(!unsubscribeAll);
  }

  /**
   *  Reset all preferences to initial state
   */
  function resetPreferences(): void {
    formMethods.reset();
  }

  // cannot render without a partition
  if (partitionOptions.length === 0) {
    return <RequiredEnabledAirgap />;
  }
  if (!isFormReady || !selectedPartition) {
    return <Skeleton />;
  }

  return (
    <>
      <Prompt
        when={formMethods.formState.isDirty}
        message={formatMessage(privacySettingMessages.unconfirmedChanges)}
      />
      <Form
        useFormMethods={formMethods}
        onSubmit={(data) => {
          handleSubmit(data);
        }}
      >
        <StyledColumnContainer style={{ gap: sp(4) }}>
          {regionalizedPurposes.length > 0 ? (
            <>
              <StyledSecondaryButton
                type="reset"
                buttonType="secondary"
                loading={loading}
                onClick={setAllPreferences}
                style={{
                  marginLeft: 'auto',
                }}
              >
                {unsubscribeAll
                  ? formatMessage(privacySettingMessages.optOutAll)
                  : formatMessage(privacySettingMessages.optInAll)}
              </StyledSecondaryButton>
              <PurposeListForm
                name="purposes"
                purposes={regionalizedPurposes}
                showPreferenceTopics={false}
              />
            </>
          ) : (
            <Card
              description={formatMessage(
                privacySettingMessages.noRegimePurposes,
              )}
              style={{ padding: sp(2) }}
              disabled
            />
          )}
          <FlexRowCenterVertical
            style={{
              marginLeft: 'auto',
              gap: sp(1),
            }}
          >
            <StyledSecondaryButton
              type="reset"
              buttonType="secondary"
              loading={loading}
              disabled={isSubmitDisabled}
              onClick={() => resetPreferences()}
            >
              {formatMessage(privacySettingMessages.resetPreferences)}
            </StyledSecondaryButton>
            <StyledPrimaryButton
              type="submit"
              buttonType="primary"
              loading={loading}
              disabled={isSubmitDisabled}
            >
              {formatMessage(privacySettingMessages.updatePreferences)}
            </StyledPrimaryButton>
          </FlexRowCenterVertical>
        </StyledColumnContainer>
      </Form>
    </>
  );
};
